import {useMemo, useState, useCallback, useRef} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {getVisitList} from '@/services/perfectStore';

const PAGE_SIZE = 10;

// Fields whose API type differs from the default 'string'.
// The DS text-filter always emits type 'string'; override per field as needed.
const FIELD_TYPE_OVERRIDES = {
  visitNumber: 'integer'
};

/**
 * Hook for fetching and managing the perfect-store visit list with
 * server-side pagination and column filtering.
 *
 * @param {string|number} targetId - Perfect Store target ID.
 * @param {number} [pageSize=10] - Number of rows per page.
 * @returns {Object} Visit list state and handlers.
 */
export const useVisitList = (targetId, initialPageSize = PAGE_SIZE) => {
  const {pageIndex, setPageIndex, setSearchArry, searchArr, handleResetAll} =
    useDataTable(0, false, true);

  const [pageSize, setPageSize] = useState(initialPageSize);

  // Track whether the most recent query reflects the current pageSize.
  // While the API is still fetching after a pageSize change, we suppress
  // the stale records so TanStack doesn't render the wrong number of rows.
  const settledPageSizeRef = useRef(initialPageSize);

  // Reset to page 0 when rows-per-page changes, then update page size.
  const handlePageSizeChange = useCallback(
    (newSize) => {
      setPageSize(newSize);
      setPageIndex(0);
    },
    [setPageIndex]
  );

  const queryParams = useMemo(
    () => ({
      sortBy: 'visitNumber',
      order: 'asc',
      pageSize,
      page: pageIndex
    }),
    [pageIndex, pageSize]
  );

  // Apply per-field type overrides so the API receives the correct type
  // (e.g. visitNumber must be 'integer', not 'string').
  const filters = useMemo(
    () =>
      searchArr.map((f) =>
        FIELD_TYPE_OVERRIDES[f.field]
          ? {...f, type: FIELD_TYPE_OVERRIDES[f.field]}
          : f
      ),
    [searchArr]
  );

  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: ['visit-list', targetId, queryParams, filters],
    queryFn: () => getVisitList(targetId, queryParams, filters),
    enabled: !!targetId
  });

  const records = useMemo(() => {
    // While a new pageSize is in-flight, the cached response belongs to the
    // previous pageSize — suppress it so TanStack doesn't render stale rows.
    if (!isFetching) {
      settledPageSizeRef.current = pageSize;
    }
    if (isFetching && settledPageSizeRef.current !== pageSize) {
      return [];
    }
    // API shape: { success: true, data: [...], totalRecords: N }
    return Array.isArray(data?.data) ? data.data : [];
  }, [data, isFetching, pageSize]);

  const totalRecords = data?.totalRecords ?? 0;

  return {
    records,
    totalRecords,
    isLoading,
    isFetching,
    pageIndex,
    pageSize,
    setPageIndex,
    handlePageSizeChange,
    setSearchArry,
    searchArr,
    handleResetAll,
    refetch
  };
};
